import { Paper, Table, TableContainer, TablePagination } from "@mui/material";
import { useState } from "react";
import { NoDataWarning } from "../common/NoDataWarning";
import { GenericTableBody } from "./GenericTableBody";
import { GenericTableHeader } from "./GenericTableHeader";
import { GenericTableProps } from "./types";
import { tableContainerStyles, tablePaginationStyles } from "./styles";

export function GenericTable<T extends Record<string, any>>({
  tableColumns,
  tableRows,
  actions,
  actionsHeaderContent,
  selectionHeaderContent,
  selectionCellContent,
  characterLimit = 30,
  styles,
  onRowClick,
  page: controlledPage,
  rowsPerPage: controlledRowsPerPage,
  onPageChange: controlledOnPageChange,
  onRowsPerPageChange: controlledOnRowsPerPageChange,
  statusVariant,
}: GenericTableProps<T>) {
  // Usa paginação controlada se fornecida, caso contrário usa paginação interna
  const isControlled = controlledPage !== undefined && controlledOnPageChange !== undefined;
  
  const [internalPage, setInternalPage] = useState(0);
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(5);

  const currentPage = isControlled ? controlledPage : internalPage;
  const itensPerPage = isControlled ? (controlledRowsPerPage ?? 5) : internalRowsPerPage;

  const handleChangePage = (_: unknown, newPage: number) => {
    if (isControlled && controlledOnPageChange) {
      controlledOnPageChange(_, newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (isControlled && controlledOnRowsPerPageChange) {
      controlledOnRowsPerPageChange(event);
    } else {
      setInternalRowsPerPage(newRowsPerPage);
      setInternalPage(0);
    }
  };

  const startIndex = currentPage * itensPerPage;
  const endIndex = startIndex + itensPerPage;

  const hasData = tableRows?.length > 0;

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          ...tableContainerStyles,
          ...(styles?.container || {}),
        }}
      >
        <Table
          stickyHeader
          sx={{
            width: "100%",
            /*
             * Desktop: tableLayout fixed distribui colunas pelas % configuradas.
             * Mobile:  tableLayout auto + minWidth garante que o conteúdo não seja
             *          comprimido — o TableContainer (overflow:auto) rola na horizontal.
             */
            tableLayout: { xs: "auto", md: "fixed" },
            minWidth: { xs: 520, md: "unset" },
          }}
          aria-label="generic table"
        >
          {hasData ? (
            <>
              <GenericTableHeader
                columns={tableColumns}
                actions={actions}
                styles={styles}
                actionsHeaderContent={actionsHeaderContent}
                selectionHeaderContent={selectionHeaderContent}
              />
              <GenericTableBody
                rowPath={tableColumns?.map((el) => el.rowName)}
                rows={tableRows}
                startIndex={startIndex}
                endIndex={endIndex}
                actions={actions}
                characterLimit={characterLimit}
                styles={styles}
                columns={tableColumns}
                onRowClick={onRowClick}
                selectionCellContent={selectionCellContent}
                statusVariant={statusVariant}
              />
            </>
          ) : (
            <NoDataWarning />
          )}
        </Table>
      </TableContainer>

      {hasData && (
        <TablePagination
          sx={tablePaginationStyles}
          labelRowsPerPage="Exibir"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count}`
          }
          rowsPerPageOptions={[1, 3, 5, 10, 20, 50, 100]}
          component="div"
          count={tableRows?.length}
          page={currentPage}
          onPageChange={handleChangePage}
          rowsPerPage={itensPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </>
  );
}
